/**
 * ClinicIntelligenceProfile (Plan v9 §4.2 prototype rework).
 *
 * Renders a per-tenant Clinic Intelligence Profile card. Mirrors the
 * prototype's "Dr. Al-Dhaheri TLP1" card:
 *   - Personalization Score (0-100) with delta-from-Day-1 callout
 *   - 4 precision metrics rendered as a 2x2 grid
 *
 * Data source: `MetricsSnapshot.clinicIntelligenceByTenant[tenantId]`.
 * Scoped to the active tenant pulled from the tenant store.
 *
 * HC2 token-only: every color, spacing, radius value resolves through
 * Signal DS tokens via Tailwind. No raw hex.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: the score region carries an `aria-label` summarizing the
 *   score + delta so SR users get the headline without having to
 *   navigate the visual hierarchy.
 */
import type { ClinicIntelligenceProfileSummary } from '../../services/metrics/types';

export interface ClinicIntelligenceProfileProps {
  /** Per-tenant profile summary. */
  profile: ClinicIntelligenceProfileSummary;
  /** Optional className for the outer wrapper. */
  className?: string;
}

const DIRECTION_CLASSES: Record<
  ClinicIntelligenceProfileSummary['precisionMetrics'][number]['direction'],
  string
> = {
  improved: 'text-status-active-fg',
  regressed: 'text-warning-strong-fg',
  unchanged: 'text-text-tertiary',
};

export function ClinicIntelligenceProfile({
  profile,
  className,
}: ClinicIntelligenceProfileProps) {
  const composed = [
    'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const scoreAriaLabel = `Personalization score ${profile.personalizationScore} of 100. Delta plus ${profile.personalizationDelta} points since Day 1.`;

  return (
    <section
      aria-labelledby="clinic-intelligence-profile-title"
      data-testid="clinic-intelligence-profile"
      data-provider={profile.providerName}
      data-clinic={profile.clinicLabel}
      className={composed}
    >
      <header className="flex flex-col gap-gutter-sm">
        <h3
          id="clinic-intelligence-profile-title"
          className="text-signal-lg font-semibold text-text-primary"
        >
          Clinic Intelligence Profile
        </h3>
        <p className="text-signal-sm text-text-secondary">
          {profile.providerName} . {profile.clinicLabel}
        </p>
      </header>

      <div
        data-testid="clinic-intelligence-profile-score"
        aria-label={scoreAriaLabel}
        className="flex flex-wrap items-baseline gap-gutter-md rounded-signal-md bg-surface-muted px-gutter-md py-gutter-md"
      >
        <div className="flex flex-col">
          <span className="text-signal-xs uppercase tracking-wide text-text-tertiary">
            Personalization Score
          </span>
          <span
            data-testid="clinic-intelligence-profile-score-value"
            className="text-signal-2xl font-semibold text-text-primary"
          >
            {profile.personalizationScore}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-signal-xs uppercase tracking-wide text-text-tertiary">
            Delta since Day 1
          </span>
          <span
            data-testid="clinic-intelligence-profile-delta"
            className="text-signal-xl font-semibold text-status-active-fg"
          >
            +{profile.personalizationDelta} pts
          </span>
        </div>
      </div>

      <ul
        aria-label="Precision metrics"
        data-testid="clinic-intelligence-profile-metrics"
        className="grid grid-cols-1 gap-gutter-sm sm:grid-cols-2"
      >
        {profile.precisionMetrics.map((metric) => (
          <li
            key={metric.label}
            data-testid={`clinic-intelligence-metric-${metric.label.replace(/\s+/g, '-').toLowerCase()}`}
            data-direction={metric.direction}
            className="flex items-baseline justify-between gap-gutter-md rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-gutter-sm"
          >
            <span className="text-signal-sm text-text-secondary">
              {metric.label}
            </span>
            <span
              className={`text-signal-lg font-semibold ${DIRECTION_CLASSES[metric.direction]}`}
            >
              {metric.value}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
